import { ChevronUp, ChevronDown, MoreHorizontal, CheckCircle2, XCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { ordersService } from '../../services/orders.service';
import { toast } from 'sonner';

// Define status type
type OrderStatus = 'PENDING' | 'CONFIRMED' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CLOSED' | 'CANCELLED' | 'REJECTED' | 'REFUNDED';

const statusConfig: Record<OrderStatus, { label: string; bgColor: string; textColor: string; icon: any }> = {
  PENDING: { label: 'New Order', bgColor: 'bg-orange-50', textColor: 'text-orange-600', icon: null },
  CONFIRMED: { label: 'Accepted', bgColor: 'bg-blue-50', textColor: 'text-blue-600', icon: CheckCircle2 },
  PREPARING: { label: 'Wait time (15-40 min)', bgColor: 'bg-purple-50', textColor: 'text-purple-600', icon: null },
  READY: { label: 'Ready', bgColor: 'bg-green-50', textColor: 'text-green-600', icon: null },
  DELIVERED: { label: 'Delivered', bgColor: 'bg-teal-50', textColor: 'text-teal-600', icon: null },
  CLOSED: { label: 'Closed', bgColor: 'bg-gray-50', textColor: 'text-gray-600', icon: null },
  CANCELLED: { label: 'Cancelled', bgColor: 'bg-red-50', textColor: 'text-red-600', icon: XCircle },
  REJECTED: { label: 'Rejected', bgColor: 'bg-red-50', textColor: 'text-red-600', icon: XCircle },
  REFUNDED: { label: 'Refunded', bgColor: 'bg-yellow-50', textColor: 'text-yellow-600', icon: null },
};

interface OrdersTableProps {
  orders: any[];
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (query: { page?: number; limit?: number }) => void;
  selectedStatus?: string;
  selectedDateRange?: string;
  customStartDate?: string;
  customEndDate?: string;
  selectedTable?: string;
  onOrderClick?: (orderId: string) => void;
  onRefresh?: () => void;
}

export function OrdersTable({ 
  orders,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  selectedStatus = 'all', 
  selectedDateRange = 'all', 
  customStartDate = '', 
  customEndDate = '', 
  selectedTable = 'all',
  onOrderClick 
}: OrdersTableProps) {
  const { formatCurrency } = useCurrency();
  const [itemsPerPage, setItemsPerPage] = useState(limit);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [perPageDropdownOpen, setPerPageDropdownOpen] = useState(false);

  useEffect(() => {
    setItemsPerPage(limit);
  }, [limit]);

  // Map backend orders to table format
  const mappedOrders = orders.map(order => {
    // Extract table name from notes if present
    const tableMatch = order.notes?.match(/Table:\s*(.+)/)?.[1];
    const tableName = tableMatch || null;
    
    // For display: Show table name or customer name
    const displayName = tableName || (order.customer 
      ? `${order.customer.firstName} ${order.customer.lastName}`
      : order.customerName || 'Guest');
    
    // For location: Show floor if it's a table order, otherwise customer location
    let location = 'N/A';
    if (tableName) {
      // Extract floor from table name (e.g., "Table 5 (Floor 1)" or just use a default)
      // For now, we'll derive floor from table number
      const tableNumber = parseInt(tableName.replace(/\D/g, ''));
      if (!isNaN(tableNumber)) {
        // Tables 1-10 = Floor 1, 11-20 = Floor 2, etc.
        const floor = Math.ceil(tableNumber / 10);
        location = `Floor ${floor}`;
      } else {
        location = 'Main Floor';
      }
    } else if (order.customer?.city && order.customer?.state) {
      location = `${order.customer.city}, ${order.customer.state}`;
    } else if (order.deliveryAddress) {
      location = order.deliveryAddress;
    }
    
    const date = new Date(order.createdAt).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    // Safely handle totalAmount - convert to number and validate
    let totalAmount = Number(order.totalAmount ?? order.total ?? 0);
    
    // If conversion fails (NaN), default to 0
    if (isNaN(totalAmount)) {
      totalAmount = 0;
    }

    return {
      id: order.orderNumber || order.id,
      date,
      customerName: displayName,
      location,
      amount: formatCurrency(totalAmount),
      status: order.status as OrderStatus,
      rawDate: new Date(order.createdAt),
      rawAmount: totalAmount,
      rawId: order.id,  // real UUID for API calls
      tableName,  // extracted table name for filtering
    };
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleDropdown = (orderId: string) => {
    setOpenDropdown(openDropdown === orderId ? null : orderId);
  };

  const handleAction = async (orderId: string, action: string) => {
    setOpenDropdown(null);
    
    try {
      // Map action to order status
      const newStatus = action === 'Accept Order' ? 'CONFIRMED' : 'REJECTED';
      
      // Update order status via API
      const response = await ordersService.updateOrderStatus(orderId, newStatus);
      
      if (response.success) {
        toast.success(`Order ${newStatus.toLowerCase()}`);
        // Note: Parent component should handle refresh
      } else {
        toast.error(`Failed to ${action.toLowerCase()}`);
      }
    } catch (error: any) {
      console.error(`Failed to ${action}:`, error);
      toast.error(error.message || `Failed to ${action.toLowerCase()}`);
    }
  };

  // Map UI status to backend status for filtering
  const getBackendStatus = (uiStatus: string): string | null => {
    const statusMap: Record<string, string> = {
      'new': 'PENDING',
      'accepted': 'CONFIRMED',
      'delivery': 'PREPARING',
      'delivered': 'DELIVERED',
      'rejected': 'CANCELLED',
    };
    return statusMap[uiStatus] || null;
  };

  // Filter orders based on selected status
  const filteredOrders = selectedStatus === 'all' 
    ? mappedOrders
    : mappedOrders.filter(order => {
        const backendStatus = getBackendStatus(selectedStatus);
        return backendStatus ? order.status === backendStatus : true;
      });

  // Filter by table (if selected)
  const tableFilteredOrders = selectedTable === 'all'
    ? filteredOrders
    : filteredOrders.filter(order => order.tableName === selectedTable);

  // Helper function to parse order date
  // const parseOrderDate = (dateStr: string): Date => {
  //   // The date is already parsed in rawDate field
  //   return new Date(dateStr);
  // };

  // Helper function to get date range
  const getDateRange = () => {
    const today = new Date(); // Use actual current date instead of hardcoded date
    today.setHours(0, 0, 0, 0);

    switch (selectedDateRange) {
      case 'today':
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);
        return { start: today, end: endOfToday };

      case 'week':
        // Get start of week (Monday)
        const startOfWeek = new Date(today);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        return { start: startOfWeek, end: endOfWeek };

      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);
        return { start: startOfMonth, end: endOfMonth };

      case 'year':
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        endOfYear.setHours(23, 59, 59, 999);
        return { start: startOfYear, end: endOfYear };

      case 'custom':
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return null;

      default:
        return null;
    }
  };

  // Filter by date range
  const dateFilteredOrders = (() => {
    const dateRange = getDateRange();
    if (!dateRange) return tableFilteredOrders;

    return tableFilteredOrders.filter(order => {
      const orderDate = order.rawDate;
      return orderDate >= dateRange.start && orderDate <= dateRange.end;
    });
  })();

  // Sort the filtered orders
  const sortedOrders = [...dateFilteredOrders].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortColumn) {
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      case 'date':
        aValue = a.rawDate.getTime();
        bValue = b.rawDate.getTime();
        break;
      case 'customer':
        aValue = a.customerName.toLowerCase();
        bValue = b.customerName.toLowerCase();
        break;
      case 'location':
        aValue = a.location.toLowerCase();
        bValue = b.location.toLowerCase();
        break;
      case 'amount':
        aValue = a.rawAmount;
        bValue = b.rawAmount;
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const totalFilteredCount = total;
  
  // Use server-side pagination - orders already contain only current page data
  const paginatedOrders = sortedOrders;
  
  const startIndex = totalFilteredCount === 0 ? 0 : (page - 1) * limit;
  const endIndex = Math.min(startIndex + paginatedOrders.length, totalFilteredCount);

  const handleItemsPerPageChange = (newValue: number) => {
    setItemsPerPage(newValue);
    setPerPageDropdownOpen(false);
    onPageChange({ page: 1, limit: newValue });
  };
  
  // Pagination handlers - call onPageChange for server fetch
  const goToPage = (pageNum: number) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange({ page: pageNum });
    }
  };
  
  const goToPrevious = () => {
    if (page > 1) {
      onPageChange({ page: page - 1 });
    }
  };
  
  const goToNext = () => {
    if (page < totalPages) {
      onPageChange({ page: page + 1 });
    }
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (page <= 3) {
        // Near start
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        // Near end
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In middle
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="text-left px-6 py-3">
                <button 
                  onClick={() => handleSort('id')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Order ID
                  <div className="flex flex-col">
                    <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === 'id' && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                    <ChevronDown className={`w-3 h-3 ${sortColumn === 'id' && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                  </div>
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button 
                  onClick={() => handleSort('date')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Date
                  <div className="flex flex-col">
                    <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === 'date' && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                    <ChevronDown className={`w-3 h-3 ${sortColumn === 'date' && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                  </div>
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button 
                  onClick={() => handleSort('customer')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Table / Customer
                  <div className="flex flex-col">
                    <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === 'customer' && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                    <ChevronDown className={`w-3 h-3 ${sortColumn === 'customer' && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                  </div>
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button 
                  onClick={() => handleSort('location')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Location
                  <div className="flex flex-col">
                    <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === 'location' && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                    <ChevronDown className={`w-3 h-3 ${sortColumn === 'location' && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                  </div>
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button 
                  onClick={() => handleSort('amount')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Amount
                  <div className="flex flex-col">
                    <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === 'amount' && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                    <ChevronDown className={`w-3 h-3 ${sortColumn === 'amount' && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                  </div>
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <button 
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Status Order
                  <div className="flex flex-col">
                    <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === 'status' && sortDirection === 'asc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                    <ChevronDown className={`w-3 h-3 ${sortColumn === 'status' && sortDirection === 'desc' ? 'text-indigo-600' : 'text-gray-400 dark:text-gray-600'}`} />
                  </div>
                </button>
              </th>
              <th className="text-left px-6 py-3">
                <span className="text-xs font-semibold text-gray-900 dark:text-white">Action</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order) => {
              const config = statusConfig[order.status as OrderStatus];
              // const StatusIcon = config.icon;
              
              return (
                <tr key={order.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-6 py-3">
                    <button 
                      onClick={() => onOrderClick?.(order.rawId)}
                      className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline transition-colors"
                    >
                      {order.id}
                    </button>
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">{order.date}</td>
                  <td className="px-6 py-3 text-xs text-gray-900 dark:text-gray-200">{order.customerName}</td>
                  <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">{order.location}</td>
                  <td className="px-6 py-3 text-xs font-medium text-gray-900 dark:text-gray-200">{order.amount}</td>
                  <td className="px-6 py-3">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${config.bgColor} dark:${config.bgColor.replace('50', '900/30')} ${config.textColor} dark:${config.textColor.replace('600', '400')}`}>
                      <span className="text-xs font-medium">{config.label}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 relative">
                    <button 
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      onClick={() => toggleDropdown(order.id)}
                    >
                      <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </button>
                    {openDropdown === order.id && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setOpenDropdown(null)}
                        ></div>
                        <div className="absolute right-6 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 py-2">
                          <button
                            className="w-full text-left px-4 py-2 text-xs font-medium text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                            onClick={() => handleAction(order.id, 'Accept Order')}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                            <span>Accept Order</span>
                          </button>
                          <button
                            className="w-full text-left px-4 py-2 text-xs font-medium text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                            onClick={() => handleAction(order.id, 'Reject Order')}
                          >
                            <XCircle className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0" />
                            <span>Reject Order</span>
                          </button>
                          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 mt-1">
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">Select an action</p>
                          </div>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {totalFilteredCount === 0
              ? 'No entries'
              : `Showing ${startIndex + 1}-${endIndex} of ${totalFilteredCount}`}
          </div>
          
          {/* Items Per Page Dropdown */}
          <div className="relative">
            <button
              onClick={() => setPerPageDropdownOpen(!perPageDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <span>{itemsPerPage} per page</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            
            {perPageDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setPerPageDropdownOpen(false)}
                ></div>
                <div className="absolute left-0 bottom-full mb-2 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                  {[10, 25, 50, 100].map((value) => (
                    <button
                      key={value}
                      onClick={() => handleItemsPerPageChange(value)}
                      className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        itemsPerPage === value ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-200'
                      }`}
                    >
                      {value} per page
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={goToPrevious}
            disabled={page === 1}
          >
            <ChevronUp className="w-3 h-3 -rotate-90" />
            Previous
          </button>
          {getPageNumbers().map(pageNum => (
            <button
              key={pageNum}
              className={`px-2.5 py-1.5 text-xs font-medium ${
                typeof pageNum === 'number' && page === pageNum ? 'text-white bg-indigo-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              } rounded-lg transition-colors`}
              onClick={() => typeof pageNum === 'number' && goToPage(pageNum)}
            >
              {pageNum}
            </button>
          ))}
          <button 
            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={goToNext}
            disabled={page === totalPages}
          >
            Next
            <ChevronUp className="w-3 h-3 rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
}